import { i18n } from '@/plugins/i18n'
import { getScoreClass } from '@/pages/analyse/score'
import { toKebabCase } from '@/utils/to-kebab-case'

// Back end now return a color instead of a kind
const kindMapping = {
  red: 'danger',
  orange: 'warning',
  green: 'ok'
}

const formatAllocationRepart = (repart) => {
  return {
    euroFund: repart.pctEuro,
    uc: repart.pctUc
  }
}

const formatTarget = (target) => {
  const formattedTarget = {
    ...target,
    stock: target.euStock + target.frStock + target.usStock + target.otherStock
  };
  // We don't want these properties in the later used graphs
  ['euStock', 'frStock', 'usStock', 'otherStock'].forEach(e => delete formattedTarget[e])
  return formattedTarget
}

const formatDetailedAllocation = (details) => {
  const formattedDetailedAllocation = {
    ...details,
    stock: {
      composition: {
        us: details.euStock,
        france: details.frStock,
        europe: details.usStock,
        other: details.otherStock
      }
    }
  };
  ['euStock', 'frStock', 'usStock', 'otherStock'].forEach(e => delete formattedDetailedAllocation[e])
  return formattedDetailedAllocation
}

const formatGeoRepartition = (repartition) => {
  const formattedGeoRepartition = { ...repartition, others: repartition.other }
  delete formattedGeoRepartition.other
  return formattedGeoRepartition
}

const formatStockSectorAllocation = (stockSectors) => {
  const formattedStockSectors = []
  Object.keys(stockSectors).forEach((key) => {
    // camelCase to kebab-case
    formattedStockSectors.push({
      name: toKebabCase(key),
      value: stockSectors[key]
    })
  })
  return formattedStockSectors
}

const formatComparePortfolios = (graphData) => {
  return graphData.map((el) => {
    const date = new Date(el[0])
    return [date.getTime(), el[1]]
  })
}

const concatTextWithoutUndefined = (texts) => {
  return texts.map(text => text).join('\n')
}

const getSummaryPoints = (allocation, performance, risk) => {
  // Summary score is an accumulation of different section steps
  // We're cumulating them and dividing them by the number of properties to get an average
  const summaryPoints = {}
  const allocationPoints = [allocation.ucRepart.pts, allocation.cgs.pts]
  // depending of the customer etfRepart isn't always sent
  if (allocation.etfRepart) allocationPoints.push(allocation.etfRepart.pts)
  summaryPoints.allocation = getScoreClass(
    allocationPoints.reduce((prev, current) => prev + current, 0) / allocationPoints.length
  )

  summaryPoints.performance = getScoreClass(performance.backtest.pts)
  summaryPoints.risk = getScoreClass(
    (risk.riskMetrics.pts + risk.contribution.pts + risk.diversification.pts) / 3
  )

  return summaryPoints
}

export function createDataBridge(data) {
  const { allocation, performance, risk } = data.analysis
  const summaryPoints = getSummaryPoints(allocation, performance, risk)

  return {
    sections: {
      allocation: {
        content: {
          2: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[1].content.title')
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: allocation.ucRepart.msg,
                    points: allocation.ucRepart.pts,
                    kind: kindMapping[allocation.ucRepart.color]
                  }
                },
                {
                  type: 'allocation-euro-fund-uc',
                  data: {
                    current: formatAllocationRepart(allocation.ucRepart.repart.current),
                    target: formatAllocationRepart(allocation.ucRepart.repart.target)
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: allocation.ucRepart.disclaimer.msg
                  }
                }
              ]
            }
          ],

          3: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[1].content.title'),
                    stepText: '1/3'
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: allocation.cgs.msg,
                    points: allocation.cgs.points,
                    kind: kindMapping[allocation.cgs.color]
                  }
                },
                {
                  type: 'allocation-composition',
                  data: {
                    current: formatTarget(allocation.cgs.repart.current.cls),
                    target: formatTarget(allocation.cgs.repart.target.cls)
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: allocation.cgs.disclaimer.c.msg
                  }
                },
                {
                  type: 'detailed-allocation',
                  data: {
                    current: formatDetailedAllocation(allocation.cgs.repart.current.cls),
                    target: formatDetailedAllocation(allocation.cgs.repart.target.cls)
                  }
                }
              ]
            },
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[2].content.title'),
                    stepText: '2/3'
                  }
                },
                {
                  // To complete
                  type: 'raised-title',
                  data: {
                    text: '',
                    kind: kindMapping['red'],
                    points: 0
                  }
                },
                {
                  type: 'basic-geo-allocation',
                  data: {
                    current: formatGeoRepartition(allocation.cgs.repart.current.geo),
                    target: formatGeoRepartition(allocation.cgs.repart.target.geo)
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: allocation.cgs.disclaimer.g.msg
                  }
                }
              ]
            },
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[3].content.title'),
                    stepText: '3/3'
                  }
                },
                {
                  // To complete
                  type: 'raised-title',
                  data: {
                    text: '',
                    kind: kindMapping['red'],
                    points: 0
                  }
                },
                {
                  type: 'stock-sectors-allocation',
                  data: {
                    current: formatStockSectorAllocation(allocation.cgs.repart.current.sec),
                    target: formatStockSectorAllocation(allocation.cgs.repart.target.sec)
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: allocation.cgs.disclaimer.s.msg
                  }
                }
              ]
            }
          ],
          4: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[3].content.title'),
                    steText: null
                  }
                },
                {
                  // To complete
                  type: 'raised-title',
                  data: {
                    text: 'PAYLOAD OU CONFLUENCE A COMPLETER',
                    kind: kindMapping['red'],
                    points: 0
                  }
                },
                {
                  type: 'uc-active-passive',
                  data: null
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: i18n.t('analyse.allocation.substeps[3].content.goal')
                  }
                }
              ]
            }
          ],
          5: [
            {
              text: allocation.ucRepart.msg,
              kind: kindMapping[allocation.ucRepart.color],
              points: allocation.ucRepart.pts
            },
            {
              text: allocation.cgs.msg,
              kind: kindMapping[allocation.cgs.color],
              points: allocation.cgs.pts
            }
          ]
        },
        points: allocation.pts // To complete
      },
      performances: {
        content: {
          2: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.performances.substeps[1].content.title'),
                    stepText: null
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: performance.backtest.msg,
                    kind: kindMapping[performance.backtest.color],
                    points: performance.points
                  }
                },
                {
                  type: 'performances-portfolio',
                  data: {
                    contract: formatComparePortfolios(performance.backtest.value.current),
                    target: formatComparePortfolios(performance.backtest.value.target)
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: performance.backtest.disclaimer.msg
                  }
                }
              ]
              // 3rd part is not relevant anymore
            }
          ],
          5: [
            {
              text: performance.backtest.msg,
              kind: kindMapping[performance.backtest.color],
              points: performance.backtest.pts
            }
          ],
          points: performance.pts
        },
        points: performance.pts,
        maxPts: performance.maxPts
      },
      risk: {
        content: {
          2: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.risk.substeps[1].content.title'),
                    step_text: null
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: risk.riskMetrics.msg,
                    kind: kindMapping[risk.riskMetrics.color],
                    points: 0
                  }
                },
                {
                  type: 'loss-exposure',
                  data: {
                    actualMdd: risk.riskMetrics.current.mdd * -1,
                    actualEsf: risk.riskMetrics.current.es * -1,
                    targetMdd: risk.riskMetrics.target.mdd * -1,
                    targetEsf: risk.riskMetrics.target.es * -1
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: i18n.t('analyse.risk.substeps[1].content.maximum-drawdown')
                  }
                }
              ]
            }
          ],
          3: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.risk.substeps[2].content.title'),
                    stepText: null
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: risk.contribution.msg,
                    kind: kindMapping[risk.contribution.color],
                    points: 0
                  }
                },
                {
                  type: 'assets-risk-contribution',
                  data: {
                    euroFund: {
                      actual: risk.contribution.value.current.euroFund,
                      target: risk.contribution.value.target.euroFund
                    },
                    stock: {
                      composition: {
                        france: {
                          actual: risk.contribution.value.current.frStock,
                          target: risk.contribution.value.target.frStock
                        },
                        europe: {
                          actual: risk.contribution.value.current.euStock,
                          target: risk.contribution.value.target.euStock
                        },
                        us: {
                          actual: risk.contribution.value.current.usStock,
                          target: risk.contribution.value.target.usStock
                        },
                        other: {
                          actual: risk.contribution.value.current.otherStock,
                          target: risk.contribution.value.target.otherStock
                        }
                      }
                    },
                    bond: {
                      actual: risk.contribution.value.current.bond,
                      target: risk.contribution.value.target.bond
                    },
                    other: {
                      actual: risk.contribution.value.current.other,
                      target: risk.contribution.value.target.other
                    }
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: i18n.t('analyse.risk.substeps[2].content.risk-managment')
                  }
                }
              ]
            }
          ],
          5: [
            {
              type: 'content-block',
              data: [
                {
                  type: 'step-title',
                  data: {
                    text: i18n.t('analyse.risk.substeps[3].content.title'),
                    stepText: null
                  }
                },
                {
                  type: 'raised-title',
                  data: {
                    text: risk.diversification.msg,
                    kind: kindMapping[risk.diversification.color],
                    points: 0
                  }
                },
                {
                  type: 'risk-notation',
                  data: {
                    score: risk.diversification.pts
                  }
                },
                {
                  type: 'disclaimer-info',
                  data: {
                    text: i18n.t('analyse.risk.substeps[3].content.clustering')
                  }
                }
              ]
            }
          ],
          6: [
            {
              text: risk.riskMetrics.msg,
              kind: kindMapping[risk.riskMetrics.color],
              points: risk.riskMetrics.pts
            },
            {
              text: risk.contribution.msg,
              kind: kindMapping[risk.contribution.color],
              points: risk.contribution.pts
            },
            {
              text: risk.diversification.msg,
              kind: kindMapping[risk.diversification.color],
              points: risk.diversification.pts
            }
          ]
        },
        points: risk.pts
      },
      summary: {
        content: {
          1: [
            {
              title: i18n.t('analyse.advices.introduction.summaries.allocation'),
              content: concatTextWithoutUndefined([allocation.ucRepart.msg, allocation.cgs.msg, allocation.etfRepart]),
              kind: summaryPoints.allocation,
              step: 'allocation'
            },
            {
              title: i18n.t('analyse.advices.introduction.summaries.performance'),
              content: performance.backtest.msg,
              kind: summaryPoints.performance,
              step: 'performances'
            },
            {
              title: i18n.t('analyse.advices.introduction.summaries.risk'),
              content: concatTextWithoutUndefined([risk.riskMetrics.msg, risk.contribution.msg, risk.diversification.msg]),
              kind: summaryPoints.risk,
              step: 'risk'
            }
          ]
        }
      },
      pts: data.analysis.pts,
      maxPts: data.analysis.maxPts
    },
    analyzedAt: 'NO DATA'
  }
}
